import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { Button } from 'dibs-elements/exports/Button.tsx';

import { useDeviceType } from '../../../nftHeader/hooks/useDeviceType.tsx';

import styles from './styles/HeroHtmlBannerText.scss';

import { type HtmlBannerText_htmlBannerExhibition$key } from './__generated__/HtmlBannerText_htmlBannerExhibition.graphql.ts';

const messages = defineMessages({
    curated: {
        id: 'NftLanding.HtmlBannerText.curated',
        defaultMessage: 'Curated by',
    },
});

type Props = {
    onClick?: () => void;
    htmlBannerExhibition: HtmlBannerText_htmlBannerExhibition$key | null | undefined;
    isTile?: boolean;
    tileCurator?: string;
    tileEyeBrow?: string | null;
    tileHeader?: string | null;
    tileDek?: string | null;
};

export const HtmlBannerText: FC<Props> = ({
    htmlBannerExhibition: htmlBannerExhibitionRef,
    onClick,
    isTile,
    tileCurator,
    tileEyeBrow,
    tileHeader,
    tileDek,
}) => {
    const { isMobile } = useDeviceType();
    const { formatMessage } = useIntl();
    const htmlBannerExhibition = useFragment(
        graphql`
            fragment HtmlBannerText_htmlBannerExhibition on NftCmsExhibitionHtmlBannerType {
                captionPosition
                captionAlignment
                eyeBrow
                header
                dek
                curator
                color
                backgroundColor
                imageSize
                ctaStyle
                ctaCopy
                ctaHref
            }
        `,
        htmlBannerExhibitionRef
    );

    const {
        captionAlignment,
        color,
        eyeBrow,
        header,
        dek,
        ctaHref,
        ctaStyle,
        ctaCopy,
        backgroundColor,
    } = htmlBannerExhibition || {};
    const curator = htmlBannerExhibition?.curator || tileCurator;
    const invertColors = color === 'white' && (!isMobile || !!backgroundColor);
    const textClasses = classnames(styles.text, {
        [styles.captionAlignCenter]: captionAlignment === 'center',
        [styles.captionColor]: invertColors,
        [styles.tile]: isTile,
    });

    const ctaClasses = classnames(styles.cta, styles.important, {
        [styles.ctaOutline]: ctaStyle === 'outlined',
        [styles.ctaColor]: invertColors,
    });

    return (
        <div className={textClasses}>
            <p className={styles.eyeBrow}>{tileEyeBrow ?? eyeBrow}</p>
            {tileHeader !== null && (
                <p
                    className={styles.heading}
                    dangerouslySetInnerHTML={header ? { __html: tileHeader ?? header } : undefined}
                />
            )}
            <p className={styles.dek}>{tileDek ?? dek}</p>
            {!isTile && ctaCopy && ctaHref && (
                <Button
                    dataTn="hero-banner-cta"
                    className={ctaClasses}
                    href={ctaHref}
                    onClick={onClick}
                    type="secondary"
                >
                    {ctaCopy}
                </Button>
            )}
            {isTile && curator && (
                <p>
                    {formatMessage(messages.curated)} {curator}
                </p>
            )}
        </div>
    );
};
